export type Size = 'S' | 'M' | 'L' | 'XL' | 'XXL'

export interface Review {
  id: string
  userName: string
  rating: number
  comment: string
  date: string
}

export interface Product {
  id: string
  name: string
  description: string
  category: string
  subcategory: string
  images: string[]
  mrp: number
  sellingPrice: number
  sizes: Record<Size, number> // Size to stock mapping
  ratings: number
  reviewCount: number
  reviews: Review[]
  tags: string[]
  isFeatured: boolean
  isNewArrival: boolean
}

export const initialProducts: Product[] = [
  {
    id: 'p1',
    name: 'Classic Oxford Cotton Shirt',
    description:
      'A timeless classic. This Oxford cotton shirt offers a perfect blend of comfort and style. Tailored for a sharp fit, it features a button-down collar and a chest pocket. Ideal for both office wear and casual outings.',
    category: 'Shirts',
    subcategory: 'Casual',
    images: [
      'https://picsum.photos/seed/shirt1a/400/500',
      'https://picsum.photos/seed/shirt1b/400/500',
      'https://picsum.photos/seed/shirt1c/400/500',
    ],
    mrp: 1999,
    sellingPrice: 1299,
    sizes: { S: 10, M: 15, L: 20, XL: 5, XXL: 0 },
    ratings: 4.5,
    reviewCount: 128,
    reviews: [
      {
        id: 'r1',
        userName: 'Rahul K.',
        rating: 5,
        comment: 'Excellent quality and fit. The fabric feels premium.',
        date: '2023-10-15',
      },
      {
        id: 'r2',
        userName: 'Amit S.',
        rating: 4,
        comment: 'Good shirt but slightly tight on the shoulders.',
        date: '2023-10-10',
      },
    ],
    tags: ['cotton', 'classic', 'office'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p2',
    name: 'Premium Linen Blend Shirt',
    description:
      'Stay cool and stylish with our premium linen blend shirt. Breathable fabric makes it perfect for summer days. Features a relaxed fit and a spread collar.',
    category: 'Shirts',
    subcategory: 'Casual',
    images: [
      'https://picsum.photos/seed/shirt2a/400/500',
      'https://picsum.photos/seed/shirt2b/400/500',
    ],
    mrp: 2499,
    sellingPrice: 1599,
    sizes: { S: 5, M: 10, L: 10, XL: 10, XXL: 2 },
    ratings: 4.2,
    reviewCount: 85,
    reviews: [
      {
        id: 'r3',
        userName: 'Vikram M.',
        rating: 4,
        comment: 'Very comfortable for summer.',
        date: '2023-09-20',
      },
    ],
    tags: ['linen', 'summer', 'casual'],
    isFeatured: false,
    isNewArrival: true,
  },
  {
    id: 'p3',
    name: 'Slim Fit Formal Check Shirt',
    description:
      'Make an impression in the boardroom with this slim-fit formal check shirt. Crafted from wrinkle-resistant cotton blend for a crisp look all day long.',
    category: 'Shirts',
    subcategory: 'Formal',
    images: [
      'https://picsum.photos/seed/shirt3a/400/500',
      'https://picsum.photos/seed/shirt3b/400/500',
      'https://picsum.photos/seed/shirt3c/400/500',
    ],
    mrp: 2199,
    sellingPrice: 1199,
    sizes: { S: 0, M: 8, L: 12, XL: 6, XXL: 0 },
    ratings: 4.7,
    reviewCount: 210,
    reviews: [
      {
        id: 'r4',
        userName: 'Sanjay P.',
        rating: 5,
        comment: 'Perfect fit for office wear. Looks very professional.',
        date: '2023-11-05',
      },
    ],
    tags: ['formal', 'check', 'office'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p4',
    name: 'Denim Casual Shirt',
    description:
      'A rugged yet refined denim shirt. Features snap buttons, dual chest pockets, and a washed finish for that perfect vintage look.',
    category: 'Shirts',
    subcategory: 'Denim',
    images: [
      'https://picsum.photos/seed/shirt4a/400/500',
      'https://picsum.photos/seed/shirt4b/400/500',
    ],
    mrp: 2799,
    sellingPrice: 1899,
    sizes: { S: 12, M: 20, L: 15, XL: 8, XXL: 4 },
    ratings: 4.6,
    reviewCount: 156,
    reviews: [],
    tags: ['denim', 'casual', 'rugged'],
    isFeatured: false,
    isNewArrival: false,
  },
  {
    id: 'p5',
    name: 'Essential Crew Neck T-Shirt',
    description:
      'The foundation of any wardrobe. Our essential crew neck t-shirt is made from ultra-soft, breathable combed cotton. Available in multiple versatile colors.',
    category: 'T-Shirts',
    subcategory: 'Basics',
    images: [
      'https://picsum.photos/seed/tshirt1a/400/500',
      'https://picsum.photos/seed/tshirt1b/400/500',
    ],
    mrp: 999,
    sellingPrice: 499,
    sizes: { S: 30, M: 45, L: 50, XL: 25, XXL: 10 },
    ratings: 4.8,
    reviewCount: 450,
    reviews: [
      {
        id: 'r5',
        userName: 'Karan J.',
        rating: 5,
        comment: 'Best basic tee I own. Bought 3 more.',
        date: '2023-10-01',
      },
    ],
    tags: ['basic', 'cotton', 'everyday'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p6',
    name: 'Graphic Print Urban T-Shirt',
    description:
      "Express yourself with our urban graphic print t-shirt. Features a bold, contemporary design on premium cotton fabric that won't fade after washing.",
    category: 'T-Shirts',
    subcategory: 'Graphic',
    images: [
      'https://picsum.photos/seed/tshirt2a/400/500',
      'https://picsum.photos/seed/tshirt2b/400/500',
      'https://picsum.photos/seed/tshirt2c/400/500',
    ],
    mrp: 1299,
    sellingPrice: 799,
    sizes: { S: 15, M: 20, L: 15, XL: 5, XXL: 0 },
    ratings: 4.3,
    reviewCount: 92,
    reviews: [],
    tags: ['graphic', 'streetwear', 'casual'],
    isFeatured: false,
    isNewArrival: true,
  },
  {
    id: 'p7',
    name: 'Polo Collar Pique T-Shirt',
    description:
      'Elevate your casual look with this classic polo t-shirt. Made from textured pique cotton, featuring a ribbed collar and cuffs for a structured fit.',
    category: 'T-Shirts',
    subcategory: 'Polo',
    images: [
      'https://picsum.photos/seed/tshirt3a/400/500',
      'https://picsum.photos/seed/tshirt3b/400/500',
    ],
    mrp: 1599,
    sellingPrice: 899,
    sizes: { S: 10, M: 25, L: 30, XL: 15, XXL: 5 },
    ratings: 4.5,
    reviewCount: 178,
    reviews: [
      {
        id: 'r6',
        userName: 'Deepak R.',
        rating: 4,
        comment: 'Good quality polo. Color is exactly as shown.',
        date: '2023-08-15',
      },
    ],
    tags: ['polo', 'smart-casual', 'pique'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p8',
    name: 'Oversized Drop Shoulder T-Shirt',
    description:
      'Embrace the streetwear trend with our oversized drop shoulder t-shirt. Relaxed fit, heavyweight cotton, and maximum comfort.',
    category: 'T-Shirts',
    subcategory: 'Oversized',
    images: [
      'https://picsum.photos/seed/tshirt4a/400/500',
      'https://picsum.photos/seed/tshirt4b/400/500',
    ],
    mrp: 1499,
    sellingPrice: 999,
    sizes: { S: 5, M: 10, L: 10, XL: 5, XXL: 2 },
    ratings: 4.6,
    reviewCount: 64,
    reviews: [],
    tags: ['oversized', 'streetwear', 'trendy'],
    isFeatured: false,
    isNewArrival: true,
  },
  {
    id: 'p9',
    name: 'Slim Fit Stretch Jeans',
    description:
      'Our signature slim fit jeans offer the perfect balance of style and comfort. Made with premium stretch denim that moves with you.',
    category: 'Jeans',
    subcategory: 'Slim Fit',
    images: [
      'https://picsum.photos/seed/jeans1a/400/500',
      'https://picsum.photos/seed/jeans1b/400/500',
      'https://picsum.photos/seed/jeans1c/400/500',
    ],
    mrp: 2999,
    sellingPrice: 1799,
    sizes: { S: 10, M: 20, L: 20, XL: 10, XXL: 5 },
    ratings: 4.4,
    reviewCount: 320,
    reviews: [
      {
        id: 'r7',
        userName: 'Rohan B.',
        rating: 5,
        comment: 'Amazing fit and very comfortable stretch.',
        date: '2023-11-10',
      },
    ],
    tags: ['denim', 'slim', 'stretch'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p10',
    name: 'Classic Straight Leg Jeans',
    description:
      'A timeless straight leg silhouette that never goes out of style. Durable, heavyweight denim designed to last for years.',
    category: 'Jeans',
    subcategory: 'Straight Fit',
    images: [
      'https://picsum.photos/seed/jeans2a/400/500',
      'https://picsum.photos/seed/jeans2b/400/500',
    ],
    mrp: 2799,
    sellingPrice: 1599,
    sizes: { S: 5, M: 15, L: 15, XL: 10, XXL: 5 },
    ratings: 4.2,
    reviewCount: 145,
    reviews: [],
    tags: ['denim', 'straight', 'classic'],
    isFeatured: false,
    isNewArrival: false,
  },
  {
    id: 'p11',
    name: 'Distressed Urban Jeans',
    description:
      'Add some edge to your look with these carefully distressed jeans. Features ripped details and a faded wash for a lived-in feel.',
    category: 'Jeans',
    subcategory: 'Distressed',
    images: [
      'https://picsum.photos/seed/jeans3a/400/500',
      'https://picsum.photos/seed/jeans3b/400/500',
    ],
    mrp: 3299,
    sellingPrice: 1999,
    sizes: { S: 8, M: 12, L: 8, XL: 4, XXL: 0 },
    ratings: 4.5,
    reviewCount: 88,
    reviews: [],
    tags: ['denim', 'distressed', 'streetwear'],
    isFeatured: false,
    isNewArrival: true,
  },
  {
    id: 'p12',
    name: 'Faux Leather Biker Jacket',
    description:
      'Channel your inner rebel with this sleek faux leather biker jacket. Features asymmetrical zip closure, multiple pockets, and a tailored fit.',
    category: 'Jackets',
    subcategory: 'Leather',
    images: [
      'https://picsum.photos/seed/jacket1a/400/500',
      'https://picsum.photos/seed/jacket1b/400/500',
      'https://picsum.photos/seed/jacket1c/400/500',
    ],
    mrp: 4999,
    sellingPrice: 2999,
    sizes: { S: 5, M: 10, L: 8, XL: 3, XXL: 1 },
    ratings: 4.8,
    reviewCount: 215,
    reviews: [
      {
        id: 'r8',
        userName: 'Arjun V.',
        rating: 5,
        comment: 'Looks and feels like real leather. Very stylish.',
        date: '2023-10-25',
      },
    ],
    tags: ['jacket', 'leather', 'biker'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p13',
    name: 'Lightweight Bomber Jacket',
    description:
      'The perfect transitional layer. This lightweight bomber jacket features a water-resistant shell, ribbed trims, and a minimalist design.',
    category: 'Jackets',
    subcategory: 'Bomber',
    images: [
      'https://picsum.photos/seed/jacket2a/400/500',
      'https://picsum.photos/seed/jacket2b/400/500',
    ],
    mrp: 3499,
    sellingPrice: 2199,
    sizes: { S: 10, M: 15, L: 15, XL: 8, XXL: 2 },
    ratings: 4.6,
    reviewCount: 132,
    reviews: [],
    tags: ['jacket', 'bomber', 'casual'],
    isFeatured: false,
    isNewArrival: true,
  },
  {
    id: 'p14',
    name: 'Fleece Pullover Hoodie',
    description:
      'Stay warm and cozy in our premium fleece pullover hoodie. Features a kangaroo pocket, drawstring hood, and a relaxed fit for ultimate comfort.',
    category: 'Hoodies',
    subcategory: 'Pullover',
    images: [
      'https://picsum.photos/seed/hoodie1a/400/500',
      'https://picsum.photos/seed/hoodie1b/400/500',
    ],
    mrp: 2299,
    sellingPrice: 1299,
    sizes: { S: 15, M: 25, L: 20, XL: 10, XXL: 5 },
    ratings: 4.7,
    reviewCount: 340,
    reviews: [
      {
        id: 'r9',
        userName: 'Nitin K.',
        rating: 5,
        comment: 'Super soft inside. Perfect for winters.',
        date: '2023-11-15',
      },
    ],
    tags: ['hoodie', 'winter', 'fleece'],
    isFeatured: true,
    isNewArrival: false,
  },
  {
    id: 'p15',
    name: 'Zip-Up Athletic Hoodie',
    description:
      'Designed for active lifestyles. This zip-up hoodie is made from moisture-wicking fabric, perfect for workouts or casual athleisure wear.',
    category: 'Hoodies',
    subcategory: 'Zip-Up',
    images: [
      'https://picsum.photos/seed/hoodie2a/400/500',
      'https://picsum.photos/seed/hoodie2b/400/500',
    ],
    mrp: 2499,
    sellingPrice: 1499,
    sizes: { S: 10, M: 15, L: 15, XL: 5, XXL: 0 },
    ratings: 4.4,
    reviewCount: 112,
    reviews: [],
    tags: ['hoodie', 'athletic', 'activewear'],
    isFeatured: false,
    isNewArrival: true,
  },
]
